<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 resource-title">Publish Planner</h5>
          <small class="text-muted">Manage planner publications by campus, class, subject, and session</small>
        </div>
      </div>

      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ paginationStart }}</strong> to
            <strong>{{ paginationEnd }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadPublications"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row publish-planner-filters mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div class="filter-fields-group filter-fields-group--cols-4 d-flex align-items-center flex-grow-1">
            <v-select
              v-model="filters.campusID"
              :options="campuses"
              :clearable="true"
              label="branch"
              :reduce="(val) => val.id"
              placeholder="Campus"
              class="filter-control filter-field-item publish-planner-filter-select planner-filter-select"
              @input="onFilterCampusChange"
            />
            <v-select
              v-model="filters.classID"
              :options="filterClassOptions"
              :clearable="true"
              label="name"
              :reduce="(val) => val.id"
              placeholder="Class"
              class="filter-control filter-field-item publish-planner-filter-select planner-filter-select"
              :loading="filterClassesLoading"
              :disabled="filterClassesLoading || !filters.campusID"
              @input="onFilterChange"
            />
            <v-select
              v-model="filters.subjectID"
              :options="filterSubjectOptions"
              :clearable="true"
              label="subject"
              :reduce="(val) => val.id"
              placeholder="Subject"
              class="filter-control filter-field-item publish-planner-filter-select planner-filter-select"
              :loading="filterSubjectsLoading"
              :disabled="filterSubjectsLoading || !filters.campusID"
              @input="onFilterChange"
            />
            <v-select
              v-model="filters.sessionID"
              :options="sessions"
              :clearable="true"
              label="session"
              :reduce="(val) => val.id"
              placeholder="Session"
              class="filter-control filter-field-item publish-planner-filter-select planner-filter-select"
              @input="onFilterChange"
            />
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openAddModal"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>Add Publication</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="publications"
          :fields="fields"
          :busy="dataLoading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 class="mt-1 mb-1 text-center">{{ $t("No records found") }}</h3>
          </template>

          <template #table-busy>
            <div class="list-skeleton">
              <div
                v-for="n in 6"
                :key="'skeleton-' + n"
                class="list-skeleton__row"
                :style="{
                  gridTemplateColumns: `repeat(${fields.length}, minmax(0, 1fr))`,
                }"
              >
                <div
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + n + '-' + index"
                  class="list-skeleton__cell"
                >
                  <div
                    class="list-skeleton__bar"
                    :style="{ width: skeletonBarWidth(index) }"
                  ></div>
                </div>
              </div>
            </div>
          </template>

          <template #cell(sno)="{ index }">
            {{ (currentPage - 1) * perPage + index + 1 }}
          </template>

          <template #cell(campusName)="data">
            <b-badge variant="light-primary">
              {{ getCampusName(data.item.campusID) }}
            </b-badge>
          </template>

          <template #cell(className)="data">
            <b-badge variant="light-primary">{{ data.value }}</b-badge>
          </template>

          <template #cell(sectionName)="data">
            <b-badge variant="light-warning">{{ data.value || "All" }}</b-badge>
          </template>

          <template #cell(subjectName)="data">
            <b-badge variant="light-success">{{ data.value }}</b-badge>
          </template>

          <template #cell(sessionName)="data">
            <b-badge variant="light-info">
              {{ data.item.sessionName || "—" }}
            </b-badge>
          </template>

          <template #cell(dtFrom)="data">
            <b-badge variant="light-info">{{ formatDate(data.value) }}</b-badge>
          </template>

          <template #cell(dtTo)="data">
            <b-badge variant="light-info">{{ formatDate(data.value) }}</b-badge>
          </template>

          <template #cell(hideDetails)="data">
            <b-badge :variant="data.value ? 'light-danger' : 'light-success'">
              {{ data.value ? "Hidden" : "Visible" }}
            </b-badge>
          </template>

          <template #head(isActive)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(isActive)="data">
            <div class="d-flex justify-content-center">
              <b-form-checkbox
                v-model="data.item.isActive"
                switch
                :disabled="!rights.edit"
                @change="toggleStatus(data.item)"
              />
            </div>
          </template>

          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex justify-content-center">
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                title="Edit"
                class="action-btn-table mr-1"
                @click="editPublication(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>

              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                title="Delete"
                class="action-btn-outline delete-btn"
                @click="deletePublication(item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Publication' : 'Add Publication'"
      @hidden="resetForm"
      centered
      size="xl"
      no-close-on-backdrop
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col md="12">
            <b-form-group label="Campus" label-for="campus">
              <v-select
                v-model="form.campusID"
                :options="campuses"
                :clearable="false"
                label="branch"
                :reduce="(val) => val.id"
                placeholder="Select campus"
                @input="onCampusChange"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Classes" label-for="classes">
              <v-select
                v-model="form.classIDs"
                :options="classOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                :multiple="true"
                placeholder="Select classes"
                @input="onClassesChange"
                :loading="modalClassesLoading"
                :disabled="!form.campusID || modalClassesLoading"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Subjects" label-for="subjects">
              <v-select
                v-model="form.subjectIDs"
                :options="subjectOptions"
                :clearable="false"
                label="subject"
                :reduce="(val) => val.id"
                :multiple="true"
                placeholder="Select subjects"
                @input="onSubjectsChange"
                :loading="modalSubjectsLoading"
                :disabled="!form.campusID || modalSubjectsLoading"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <b-form-group label="Session *" label-for="session">
              <v-select
                v-model="form.sessionID"
                :options="sessions"
                :clearable="false"
                label="session"
                :reduce="(val) => val.id"
                placeholder="Select session"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <b-form-group label="Date Range" label-for="dateRange">
              <flat-pickr
                v-model="dateRange"
                :config="dateConfig"
                class="form-control"
                placeholder="Select date range"
                @on-change="onDateRangeChange"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <b-form-group>
              <b-form-checkbox v-model="form.hideDetails">
                Hide Details
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="savePublication"
          :disabled="loading"
          class="btn-primary-custom"
        >
          <b-spinner v-if="loading" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  BFormCheckbox,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";
import axios from "axios";
import plannerFilterDropdownScrollMixin from "./plannerFilterDropdownScroll";

export default {
  name: "PublishPlanner",
  mixins: [plannerFilterDropdownScrollMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    BFormCheckbox,
    BBadge,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      publications: [],
      campuses: [],
      classes: [],
      sections: [],
      subjects: [],
      sessions: [],
      modalSections: [],
      modalClasses: [],
      modalSubjects: [],
      filterClasses: [],
      filterSubjects: [],
      filterClassesLoading: false,
      filterSubjectsLoading: false,
      searchQuery: "",
      showModal: false,
      isEdit: false,
      loading: false,
      dataLoading: false,
      modalClassesLoading: false,
      modalSubjectsLoading: false,
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      fields: [
        { key: "sno", label: "S.No", thClass: "text-center", tdClass: "text-center" },
        { key: "campusName", label: "Campus" },
        { key: "className", label: "Class" },
        { key: "sectionName", label: "Section" },
        { key: "subjectName", label: "Subject" },
        { key: "sessionName", label: "Session" },
        { key: "dtFrom", label: "From Date" },
        { key: "dtTo", label: "To Date" },
        { key: "hideDetails", label: "Details Status" },
        { key: "isActive", label: "Status", thClass: "text-center", tdClass: "text-center" },
        { key: "actions", label: "Actions", thClass: "text-center", tdClass: "text-center" },
      ],
      form: {
        id: 0,
        campusID: null,
        classIDs: [],
        sectionID: null,
        subjectIDs: [],
        sessionID: null,
        dtFrom: "",
        dtTo: "",
        hideDetails: false,
        isActive: true,
      },
      filters: {
        campusID: null,
        classID: null,
        subjectID: null,
        sectionID: null,
        sessionID: null,
      },

      // Date picker config
      dateConfig: {
        mode: "range",
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      dateRange: "",
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    plannerCampusID() {
      return this.reportDomain === "myskooliuss"
        ? 1
        : this.$store.state.userData.cId;
    },
    // Add 'All classes' option dynamically on top
    classOptions() {
      const options = this.modalClasses ? [...this.modalClasses] : [];
      return [{ id: "ALL", name: "All classes" }, ...options];
    },
    // Add 'All subjects' option dynamically on top
    subjectOptions() {
      const options = this.modalSubjects ? [...this.modalSubjects] : [];
      return [{ id: "ALL", subject: "All subjects" }, ...options];
    },
    // Filter options for classes based on selected campus
    filterClassOptions() {
      if (!this.filters.campusID) {
        return this.classes; // Show all classes if no campus selected
      }
      return this.filterClasses;
    },
    // Filter options for subjects based on selected campus
    filterSubjectOptions() {
      if (!this.filters.campusID) {
        return this.subjects; // Show all subjects if no campus selected
      }
      return this.filterSubjects;
    },
    // Form validation
    isFormValid() {
      return (
        this.form.campusID !== null &&
        this.form.campusID !== undefined &&
        Array.isArray(this.form.classIDs) &&
        this.form.classIDs.length > 0 &&
        Array.isArray(this.form.subjectIDs) &&
        this.form.subjectIDs.length > 0 &&
        this.form.sessionID &&
        this.dateRange &&
        this.dateRange !== "" &&
        this.form.dtFrom &&
        this.form.dtFrom !== "" &&
        this.form.dtTo &&
        this.form.dtTo !== ""
      );
    },
    paginationStart() {
      const total = Number(this.totalCount) || 0;
      if (total <= 0) return 0;
      const page = Number(this.currentPage) || 1;
      const perPage = Number(this.perPage) || 20;
      return (page - 1) * perPage + 1;
    },
    paginationEnd() {
      const total = Number(this.totalCount) || 0;
      if (total <= 0) return 0;
      const page = Number(this.currentPage) || 1;
      const perPage = Number(this.perPage) || 20;
      return Math.min(page * perPage, total);
    },
  },
  created() {
    // Initialize rights from store
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      this.loadPublications();
      this.loadCampuses();
      this.loadSessions();
      this.loadClasses();
      this.loadSections();
      this.loadSubjects();
    }
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    skeletonBarWidth(index) {
      const widths = ["42%", "72%", "58%", "65%", "80%", "70%", "48%", "40%"];
      return widths[index % widths.length];
    },
    async loadCampuses() {
      const obj = {
        url:
          this.$store.state.domain +
          "campuses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.campuses = await this.get(obj);
    },

    getCampusName(campusID) {
      if (!campusID || !this.campuses.length) return "Unknown Campus";
      const campus = this.campuses.find((c) => c.id === campusID);
      return campus ? campus.branch : "Unknown Campus";
    },

    async loadSessions() {
      try {
        const url = `${this.$store.state.domain}Sessions?db=${this.$store.state.userData.db}&cID=${this.plannerCampusID}`;
        const res = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        if (res.data?.status === "success" && Array.isArray(res.data.data)) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data?.data)) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data)) {
          this.sessions = res.data;
        } else {
          this.sessions = [];
        }
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },

    getDefaultSessionID() {
      if (!this.sessions.length) return null;
      const currentSession = this.sessions.find(
        (el) => el.isCurrentSession === true
      );
      return currentSession
        ? currentSession.id
        : this.sessions[this.sessions.length - 1].id;
    },

    async loadClasses() {
      const obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      const classesData = await this.get(obj);
      this.classes = classesData.map((el) => el.cls);
    },

    async loadSections() {
      const obj = {
        url:
          this.$store.state.domain +
          "sections?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.sections = await this.get(obj);
    },

    async loadSubjects() {
      const obj = {
        url:
          this.$store.state.domain +
          "Subjects?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.subjects = await this.get(obj);
    },

    async loadModalClasses() {
      if (!this.form.campusID) return;

      this.modalClassesLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.form.campusID +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.modalClasses = response.map((el) => el.cls);
      } catch (error) {
        console.error("Error loading modal classes:", error);
        this.modalClasses = [];
      } finally {
        this.modalClassesLoading = false;
      }
    },

    async loadModalSubjects() {
      if (!this.form.campusID) return;

      this.modalSubjectsLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.form.campusID,
          token: this.$store.state.userData.token,
        };
        this.modalSubjects = await this.get(obj);
      } catch (error) {
        console.error("Error loading modal subjects:", error);
        this.modalSubjects = [];
      } finally {
        this.modalSubjectsLoading = false;
      }
    },

    async loadFilterClasses() {
      if (!this.filters.campusID) {
        this.filterClasses = [];
        return;
      }

      this.filterClassesLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.filters.campusID +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.filterClasses = response.map((el) => el.cls);
      } catch (error) {
        console.error("Error loading filter classes:", error);
        this.filterClasses = [];
      } finally {
        this.filterClassesLoading = false;
      }
    },

    async loadFilterSubjects() {
      if (!this.filters.campusID) {
        this.filterSubjects = [];
        return;
      }

      this.filterSubjectsLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.filters.campusID,
          token: this.$store.state.userData.token,
        };
        this.filterSubjects = await this.get(obj);
      } catch (error) {
        console.error("Error loading filter subjects:", error);
        this.filterSubjects = [];
      } finally {
        this.filterSubjectsLoading = false;
      }
    },

    async loadPublications() {
      this.dataLoading = true;
      const obj = {
        url:
          this.$store.state.domain +
          "PlannerPublication/GetByPagination?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.$store.state.userData.cId +
          "&page=" +
          this.currentPage +
          "&pageSize=" +
          this.perPage +
          "&filterCampusID=" +
          (this.filters.campusID || "") +
          "&classID=" +
          (this.filters.classID || "") +
          "&subjectID=" +
          (this.filters.subjectID || "") +
          "&sectionID=" +
          (this.filters.sectionID || "") +
          "&sessionID=" +
          (this.filters.sessionID || ""),
        token: this.$store.state.userData.token,
        dashboard: true,
      };
      let res = await this.get(obj);
      if (res && Array.isArray(res.data)) {
        this.publications = res.data;
        this.totalCount = res.total;
      } else {
        this.publications = [];
        this.totalCount = 0;
      }
      this.dataLoading = false;
    },

    async onFilterChange() {
      this.currentPage = 1; // Reset to first page when filters change
      this.loadPublications();
    },

    async onFilterCampusChange() {
      // Reset class and subject filters when campus changes
      this.filters.classID = null;
      this.filters.subjectID = null;
      this.filterClasses = [];
      this.filterSubjects = [];

      // Load classes and subjects for the selected campus
      if (this.filters.campusID) {
        await this.loadFilterClasses();
        await this.loadFilterSubjects();
      }

      // Reload publications with new filters
      this.currentPage = 1;
      this.loadPublications();
    },

    async onCampusChange() {
      // Reset form data and loading states
      this.form.classIDs = [];
      this.form.sectionID = null;
      this.form.subjectIDs = [];
      this.modalSections = [];
      this.modalClasses = [];
      this.modalSubjects = [];

      // Load classes and subjects for selected campus
      if (this.form.campusID) {
        await this.loadModalClasses();
        await this.loadModalSubjects();
      }
    },

    onClassesChange(values) {
      if (!Array.isArray(values)) return;
      if (values.includes("ALL")) {
        // Replace with all available class IDs
        const allIds = (this.modalClasses || []).map((c) => c.id);
        this.$nextTick(() => {
          this.form.classIDs = allIds;
        });
      }
    },

    onSubjectsChange(values) {
      if (!Array.isArray(values)) return;
      if (values.includes("ALL")) {
        // Replace with all available subject IDs
        const allIds = (this.modalSubjects || []).map((s) => s.id);
        this.$nextTick(() => {
          this.form.subjectIDs = allIds;
        });
      }
    },

    onClassChange() {
      // Load sections for selected class - keeping this for backward compatibility
      // Since we now use multiselect, this method might not be needed
      // But keeping it in case sections are still used elsewhere
      if (this.form.classIDs && this.form.classIDs.length > 0) {
        // For multiselect, we'll default to "All" sections
        this.modalSections = [{ id: 0, section: "All" }];
        this.$nextTick(() => {
          this.form.sectionID = 0;
        });
      } else {
        this.modalSections = [];
        this.form.sectionID = null;
      }
    },

    openAddModal() {
      this.isEdit = false;
      this.modalClasses = [];
      this.modalSubjects = [];
      this.modalClassesLoading = false;
      this.modalSubjectsLoading = false;
      this.dateRange = "";
      this.form.sessionID = this.getDefaultSessionID();
      this.showModal = true;
    },

    async editPublication(item) {
      this.isEdit = true;
      this.form = {
        ...item,
        // Convert single values to arrays for multiselect
        classIDs: item.classID ? [item.classID] : [],
        subjectIDs: item.subjectID ? [item.subjectID] : [],
        dtFrom: this.formatDateForInput(item.dtFrom),
        dtTo: this.formatDateForInput(item.dtTo),
      };

      // Set date range for editing
      if (this.form.dtFrom && this.form.dtTo) {
        this.dateRange = this.form.dtFrom + " to " + this.form.dtTo;
      }

      // Load modal data based on the item's campus
      if (this.form.campusID) {
        await this.loadModalClasses();
        await this.loadModalSubjects();
      }

      // Load sections for the selected class
      this.onClassChange();

      this.showModal = true;
    },

    async deletePublication(item) {
      const result = await this.$swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        const obj = {
          url:
            this.$store.state.domain +
            "PlannerPublication/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Publication deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const success = await this.delete(obj);
        if (success) {
          this.loadPublications();
        }
      }
    },

    onDateRangeChange(selectedDates) {
      if (selectedDates && selectedDates.length === 2) {
        // Format dates to avoid timezone issues
        this.form.dtFrom = this.formatDateForInput(selectedDates[0]);
        this.form.dtTo = this.formatDateForInput(selectedDates[1]);
      } else {
        // Clear the dates if no valid selection
        this.form.dtFrom = "";
        this.form.dtTo = "";
      }
    },

    async savePublication() {
      // Check each required field individually
      if (
        !this.form.campusID ||
        this.form.campusID === null ||
        this.form.campusID === undefined
      ) {
        this.$bvToast.toast("Please select a campus!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (
        !this.form.classIDs ||
        !Array.isArray(this.form.classIDs) ||
        this.form.classIDs.length === 0
      ) {
        this.$bvToast.toast("Please select at least one class!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (
        !this.form.subjectIDs ||
        !Array.isArray(this.form.subjectIDs) ||
        this.form.subjectIDs.length === 0
      ) {
        this.$bvToast.toast("Please select at least one subject!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (!this.form.sessionID) {
        this.$bvToast.toast("Please select a session!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (
        !this.dateRange ||
        this.dateRange === null ||
        this.dateRange === undefined ||
        this.dateRange === ""
      ) {
        this.$bvToast.toast("Please select a date range!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (
        !this.form.dtFrom ||
        this.form.dtFrom === null ||
        this.form.dtFrom === undefined ||
        this.form.dtFrom === ""
      ) {
        this.$bvToast.toast("Please select a valid date range!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (
        !this.form.dtTo ||
        this.form.dtTo === null ||
        this.form.dtTo === undefined ||
        this.form.dtTo === ""
      ) {
        this.$bvToast.toast("Please select a valid date range!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;

      // Prepare the payload according to the new structure
      const payload = {
        campusID: this.form.campusID,
        classIDs: this.form.classIDs,
        subjectIDs: this.form.subjectIDs,
        sessionID: this.form.sessionID,
        dtFrom: this.form.dtFrom,
        dtTo: this.form.dtTo,
        hideDetails: this.form.hideDetails,
        isActive: this.form.isActive,
      };

      const obj = {
        url:
          this.$store.state.domain +
          "PlannerPublication/Save?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.form.campusID +
          "&userID=" +
          this.$store.state.userData.userID,
        body: payload,
        message:
          "Publication " +
          (this.isEdit ? "updated" : "created") +
          " successfully.",
        context: this,
        token: this.$store.state.userData.token,
      };

      const success = await this.post(obj);

      if (success) {
        this.showModal = false;
        this.loadPublications();
      }
      this.loading = false;
    },

    resetForm() {
      this.form = {
        id: 0,
        campusID: null,
        classIDs: [],
        sectionID: null,
        subjectIDs: [],
        sessionID: this.getDefaultSessionID(),
        dtFrom: "",
        dtTo: "",
        hideDetails: false,
        isActive: true,
      };
      this.modalSections = [];
      this.modalClasses = [];
      this.modalSubjects = [];
      this.modalClassesLoading = false;
      this.modalSubjectsLoading = false;
      this.dateRange = "";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString();
    },

    formatDateForInput(dateString) {
      if (!dateString) return "";
      // Create date object and adjust for timezone to avoid date shifting
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    async toggleStatus(item) {
      try {
        // Use the item's campusID instead of the logged-in user's cId
        const campusID = item.campusID || this.$store.state.userData.cId;

        const obj = {
          url:
            this.$store.state.domain +
            "PlannerPublication/UpdateStatus?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            campusID +
            "&id=" +
            item.id +
            "&status=" +
            item.isActive,
          message: "Status updated successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const success = await this.post(obj);
        if (success) {
          this.loadPublications();
        }
      } catch (error) {
        console.error("Error toggling status:", error);
        this.$bvToast.toast("Failed to update status", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
    },

    onPageSizeChange(value) {
      this.perPage = Number(value) || 20;
      this.currentPage = 1;
      this.loadPublications();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./plannerListTable.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

// Global z-index fixes for modal dropdowns
:deep(.vs__dropdown-menu) {
  z-index: 9999 !important;
  position: absolute !important;
}

:deep(.vs--open .vs__dropdown-menu) {
  z-index: 9999 !important;
  position: absolute !important;
}

:deep(.vs__dropdown-toggle) {
  position: relative;
  z-index: 10;
}

:deep(.vs--open .vs__dropdown-toggle) {
  z-index: 9999;
}

:deep(.modal) {
  z-index: 1050;
}

:deep(.modal-backdrop) {
  z-index: 1040;
}

:deep(.modal-dialog) {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

:deep(.modal-content) {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// Modal Height and Scrollability
.modal-content-custom {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1050;
  overflow: hidden;
}

.modal-body-custom {
  max-height: calc(90vh - 140px);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 24px;
  position: relative;
  z-index: 1;
  flex: 1;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f8f9fa;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #dee2e6;
    border-radius: 4px;
    border: 2px solid #f8f9fa;

    &:hover {
      background: #adb5bd;
    }
  }

  // Ensure proper scrolling behavior
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;

  // Fix for proper scrolling in modal
  overscroll-behavior: contain;
}

// Update button styling to match Add button (purple gradient)
.btn-primary-custom {
  background: linear-gradient(135deg, #7367f0 0%, #5a52d5 100%) !important;
  border: none !important;
  color: white !important;
  font-weight: 500 !important;

  &:hover {
    background: linear-gradient(135deg, #5a52d5 0%, #4a42c7 100%) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.3);
  }

  &:disabled {
    background: #6c757d !important;
    opacity: 0.6;
    transform: none;
    box-shadow: none;
  }
}

// Enhanced Modal Styling
.publication-form {
  .card {
    transition: all 0.3s ease;
    border-radius: 12px;
    margin-bottom: 16px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
    }

    .card-header {
      border-radius: 12px 12px 0 0 !important;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
      padding: 12px 16px;

      h6 {
        color: #495057 !important;
        font-weight: 600;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
      }

      small {
        font-size: 0.75rem;
        opacity: 0.8;
      }
    }

    .card-body {
      border-radius: 0 0 12px 12px;
      padding: 16px;
    }
  }

  // Enhanced v-select styling with proper z-index
  .campus-select,
  .classes-select,
  .subjects-select {
    position: relative;
    z-index: 10;

    .vs__dropdown-toggle {
      border-radius: 8px;
      border: 2px solid #e9ecef;
      transition: all 0.3s ease;
      position: relative;
      z-index: 10;

      &:hover {
        border-color: #007bff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.1);
      }

      &:focus-within {
        border-color: #007bff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      }
    }

    .vs__selected-options {
      .vs__selected {
        background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
        border: none;
        border-radius: 20px;
        color: white;
        font-weight: 500;
        padding: 4px 12px;
        margin: 2px;

        .vs__deselect {
          color: white;
          opacity: 0.8;

          &:hover {
            opacity: 1;
            background: rgba(255, 255, 255, 0.2);
            border-radius: 50%;
          }
        }
      }
    }

    .vs__dropdown-menu {
      border-radius: 8px;
      border: 2px solid #e9ecef;
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
      position: absolute;
      z-index: 9999 !important;
      background: white;
      max-height: 200px;
      overflow-y: auto;

      .vs__dropdown-option {
        padding: 12px 16px;
        transition: all 0.2s ease;

        &:hover {
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
          color: white;
          transform: translateX(4px);
        }

        &.vs__dropdown-option--highlight {
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
          color: white;
        }
      }
    }

    // Ensure dropdown appears above modal when open
    &.vs--open {
      z-index: 9999;

      .vs__dropdown-menu {
        z-index: 9999 !important;
      }
    }
  }

  // Enhanced date picker styling
  .date-range-input {
    border-radius: 8px;
    border: 2px solid #e9ecef;
    transition: all 0.3s ease;

    &:hover {
      border-color: #007bff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.1);
    }

    &:focus {
      border-color: #007bff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }

  // Enhanced form text styling
  .form-text {
    font-size: 0.8rem;
    margin-top: 8px;
    padding: 8px 12px;
    background: #f8f9fa;
    border-radius: 6px;
    border-left: 3px solid #007bff;
  }

  // Enhanced checkbox styling
  .custom-control-input:checked ~ .custom-control-label::before {
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
    border-color: #007bff;
  }
}

// Modal header enhancement
.modal-header {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
  border-radius: 12px 12px 0 0 !important;
  padding: 20px 24px !important;

  h5 {
    color: white !important;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  .close {
    color: white !important;
    opacity: 0.8;
    transition: all 0.3s ease;

    &:hover {
      opacity: 1;
      transform: scale(1.1);
    }
  }
}

// Modal footer enhancement
.modal-footer {
  background: #f8f9fa;
  border-radius: 0 0 12px 12px !important;
  padding: 20px 24px !important;
  border-top: 1px solid #e9ecef;

  .btn {
    border-radius: 8px;
    font-weight: 500;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &.btn-primary {
      background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
      border: none;

      &:hover {
        background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
      }

      &:disabled {
        background: #6c757d;
        opacity: 0.6;
        transform: none;
        box-shadow: none;
      }
    }

    &.btn-outline-secondary {
      border: 2px solid #6c757d;
      color: #6c757d;

      &:hover {
        background: #6c757d;
        border-color: #6c757d;
        color: white;
      }
    }
  }
}

// Loading state enhancement
.vs__spinner {
  border-color: #007bff transparent #007bff transparent;
}

// Responsive adjustments
@media (max-width: 768px) {
  .modal-content-custom {
    max-height: 95vh;
    margin: 10px;
  }

  .modal-body-custom {
    max-height: calc(95vh - 100px);
    padding: 16px;
  }

  .publication-form {
    .card {
      margin-bottom: 12px;

      .card-header {
        padding: 10px 12px;

        h6 {
          font-size: 0.9rem;
        }

        small {
          font-size: 0.7rem;
        }
      }

      .card-body {
        padding: 12px;
      }
    }

    // Stack columns on mobile
    .row {
      .col-md-6 {
        margin-bottom: 16px;
      }
    }

    .modal-footer {
      flex-direction: column;
      gap: 12px;
      padding: 16px;

      .btn {
        width: 100%;
        font-size: 0.9rem;
      }
    }
  }
}

@media (max-width: 576px) {
  .modal-content-custom {
    max-height: 98vh;
    margin: 5px;
  }

  .modal-body-custom {
    max-height: calc(98vh - 80px);
    padding: 12px;
  }

  .publication-form {
    .card {
      .card-header {
        padding: 8px 10px;

        h6 {
          font-size: 0.85rem;
        }
      }

      .card-body {
        padding: 10px;
      }
    }

    .form-text {
      font-size: 0.7rem;
      padding: 6px 8px;
    }
  }
}

</style>

<style lang="scss">
@import "./plannerFilterDropdownMenu.scss";
</style>
